import { useCallback, useState } from 'react';
import { showApiErrorToast, notify } from '@packages/shared/utils';
import type { InternalTableSchema, InternalConfirmConfig } from '../core/schema/types';
import { ActionRegistry } from '../registry/ActionRegistry';
import { mergeRowForValidation } from './mergeRowForValidation';
import { rowHasSemanticDeltas } from '../utils/editRowUtils';

function applyPayloadMapper(
  rows: Record<string, unknown>[],
  mapper: Readonly<Record<string, string>> | null
): Record<string, unknown>[] {
  if (mapper == null) return rows;
  return rows.map((row) => {
    const mapped: Record<string, unknown> = {};
    for (const [payloadKey, rowField] of Object.entries(mapper)) {
      mapped[payloadKey] = row[rowField];
    }
    return mapped;
  });
}

/**
 * Maps field errors from a saveActionId response to a row-keyed error map.
 * Returns null if no inline field errors found (caller should show generic toast).
 */
function mapSaveActionErrors(
  error: any,
  rowKeys: string[]
): Map<string, Record<string, string>> | null {
  const errors: Array<{ sNo: string | null; fieldName: string; error: string }> =
    error?.errors ?? [];
  const hasInlineErrors = errors.some((e) => e.fieldName && e.sNo != null);
  if (!hasInlineErrors) return null;

  const errorMap = new Map<string, Record<string, string>>();
  for (const err of errors) {
    if (!err.fieldName) continue;
    let targetKey: string | undefined;
    if (err.sNo != null && err.sNo.includes('.')) {
      const [parentIdx, subIdx] = err.sNo.split('.');
      const parentKey = rowKeys[Number(parentIdx)];
      targetKey = parentKey ? `${parentKey}.${subIdx}` : rowKeys[0];
    } else {
      targetKey =
        err.sNo != null && rowKeys[Number(err.sNo)]
          ? rowKeys[Number(err.sNo)]
          : rowKeys[0];
    }
    if (!targetKey) continue;
    const existing = errorMap.get(targetKey) ?? {};
    existing[err.fieldName] = err.error;
    errorMap.set(targetKey, existing);
  }
  return errorMap;
}

function parentRowsHaveErrors(editErrors: Map<string, Record<string, string>>): boolean {
  for (const [key, errors] of editErrors) {
    if (!key.includes('.') && Object.keys(errors).length > 0) {
      return true;
    }
  }
  return false;
}

interface UseTableSaveParams {
  internalSchema: InternalTableSchema;
  tableState: {
    editingRows: Map<string, Record<string, unknown>>;
    editErrors: Map<string, Record<string, string>>;
    data: unknown[];
  };
  /** Read latest table state at save time (avoids stale closure in imperative saveAll). */
  getTableState?: () => {
    editingRows: Map<string, Record<string, unknown>>;
    editErrors: Map<string, Record<string, string>>;
    data: unknown[];
  };
  stateActions: {
    applyEdit: (rowKey: string) => void;
    cancelEditing: (rowKey: string) => void;
    cancelEditingBatch: (keys: string[]) => void;
    cancelAllEditing: () => void;
    clearServerErrors: () => void;
    setServerErrors: (errors: Map<string, Record<string, string>>) => void;
    setRowEditErrors?: (rowKey: string, errors: Record<string, string>) => void;
  };
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  getSubRowKeysForParent: (parentKey: string) => string[];
  onSave?: (rows: Record<string, unknown>[], allData: unknown[]) => Promise<void>;
  validateEditedRow?: (row: Record<string, unknown>, rowKey?: string) => Record<string, string>;
  isSaveLoading?: boolean;
  refreshTable?: () => void;
  executeSaveAction: (
    rows: Record<string, unknown>[],
    endpoint: string,
    method?: string,
    onSuccess?: string,
    successMessage?: string
  ) => Promise<
    | { ok: boolean; errors?: Array<{ sNo: string | null; field: string; message: string }> }
    | undefined
  >;
  applyComputeRules?: (
    row: Record<string, unknown>,
    changedField: string
  ) => Record<string, unknown>;
  computeSourceFields?: ReadonlyArray<string>;
  onRowSaved?: (row: Record<string, unknown>, rowKey: string, mode: 'create' | 'update') => void;
}

interface UseTableSaveReturn {
  internalSaveLoading: boolean;
  effectiveSaveLoading: boolean;
  pendingSaveConfirm: {
    config: InternalConfirmConfig;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
  } | null;
  handleRowSave: (rowKey: string) => Promise<void>;
  handleRowCancel: (rowKey: string) => void;
  handleBulkSave: () => Promise<void>;
}

export function useTableSave({
  internalSchema,
  tableState,
  getTableState,
  stateActions,
  displayData,
  resolveRowKey,
  getSubRowKeysForParent,
  onSave,
  validateEditedRow,
  isSaveLoading,
  executeSaveAction,
  refreshTable: refreshTableFn,
  applyComputeRules,
  computeSourceFields,
  onRowSaved,
}: UseTableSaveParams): UseTableSaveReturn {
  const [internalSaveLoading, setInternalSaveLoading] = useState(false);
  const effectiveSaveLoading = isSaveLoading ?? internalSaveLoading;

  const [pendingSaveConfirm, setPendingSaveConfirm] = useState<{
    config: InternalConfirmConfig;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
  } | null>(null);

  const confirmSave = useCallback((): Promise<boolean> => {
    const cfg: InternalConfirmConfig = internalSchema.dataSource?.confirmBeforeSave ?? {
      title: 'Are you sure you want to Save?',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      variant: 'warning',
      titleAlign: 'center',
    };
    return new Promise<boolean>((resolve) => {
      setPendingSaveConfirm({
        config: cfg,
        onConfirm: () => {
          setPendingSaveConfirm((prev) => (prev ? { ...prev, isLoading: true } : null));
          resolve(true);
        },
        onCancel: () => {
          setPendingSaveConfirm(null);
          resolve(false);
        },
      });
    });
  }, [internalSchema.dataSource?.confirmBeforeSave]);

  const dismissSaveConfirm = useCallback(() => {
    setPendingSaveConfirm(null);
  }, []);

  const applyRowValidation = useCallback(
    (rows: Array<{ rowKey: string; mergedRow: Record<string, unknown> }>): boolean => {
      if (!validateEditedRow || !stateActions.setRowEditErrors) return true;

      const subField = internalSchema.features.rows.subRows?.dataField;
      let valid = true;
      for (const { rowKey, mergedRow } of rows) {
        const errors = validateEditedRow(mergedRow, rowKey);
        stateActions.setRowEditErrors(rowKey, errors);
        if (Object.keys(errors).length > 0) {
          valid = false;
        }

        if (subField) {
          const subRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
          for (let idx = 0; idx < subRows.length; idx++) {
            const subRowKey = `${rowKey}.${idx}`;
            if (!tableState.editingRows.has(subRowKey)) continue;
            const subRowRec = subRows[idx] as Record<string, unknown>;
            const subErrors = validateEditedRow(subRowRec, subRowKey);
            stateActions.setRowEditErrors(subRowKey, subErrors);
            if (Object.keys(subErrors).length > 0) {
              valid = false;
            }
          }
        }
      }
      return valid;
    },
    [
      validateEditedRow,
      stateActions,
      internalSchema.features.rows.subRows?.dataField,
      tableState.editingRows,
    ]
  );

  const handleRowSave = useCallback(
    async (rowKey: string) => {
      const editedValues = tableState.editingRows.get(rowKey);
      const subRowKeys = getSubRowKeysForParent(rowKey);
      const hasSubRowEdits = subRowKeys.length > 0;
      if (!editedValues && !hasSubRowEdits) return;

      let rowSNo = -1;
      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        if (resolveRowKey(rec, idx) === rowKey) { rowSNo = idx; return true; }
        return false;
      }) as Record<string, unknown> | undefined;

      let mergedRow: Record<string, unknown> = mergeRowForValidation(
        original,
        editedValues ?? {},
        applyComputeRules,
        computeSourceFields,
        internalSchema.computeRules
      );

      const subField = internalSchema.features.rows.subRows?.dataField;
      if (subField && subRowKeys.length > 0) {
        const originalSubRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
        const updatedSubRows = originalSubRows.map((sr, idx) => {
          const subKey = `${rowKey}.${idx}`;
          const subEdits = tableState.editingRows.get(subKey);
          const merged = subEdits ? { ...(sr as Record<string, unknown>), ...subEdits } : (sr as Record<string, unknown>);
          return rowSNo !== -1 ? { ...merged, sNo: `${rowSNo}.${idx}` } : merged;
        });
        mergedRow = { ...mergedRow, [subField]: updatedSubRows };
      }

      if (rowSNo !== -1) {
        mergedRow = { ...mergedRow, sNo: String(rowSNo) };
      }

      if (!applyRowValidation([{ rowKey, mergedRow }])) return;

      const confirmed = await confirmSave();
      if (!confirmed) return;

      const cleanupEditing = () => {
        stateActions.applyEdit(rowKey);
        if (subRowKeys.length > 0) {
          stateActions.cancelEditingBatch(subRowKeys);
        }
      };

      if (onSave) {
        setInternalSaveLoading(true);
        try {
          await onSave([mergedRow], tableState.data);
          cleanupEditing();
          stateActions.clearServerErrors();
          onRowSaved?.(mergedRow, rowKey, 'update');
        } catch (error) {
          showApiErrorToast(error);
        } finally {
          setInternalSaveLoading(false);
          dismissSaveConfirm();
        }
      } else if (
        internalSchema.dataSource?.saveActionId &&
        ActionRegistry.hasAction(internalSchema.dataSource.saveActionId)
      ) {
        const actionId = internalSchema.dataSource.saveActionId;
        setInternalSaveLoading(true);
        try {
          await ActionRegistry.executeActionRaw(actionId, {
            pk: rowKey,
            row: mergedRow,
            data: { editedRows: [mergedRow] },
            refreshTable: refreshTableFn ?? (() => {}),
          });
          cleanupEditing();
          stateActions.clearServerErrors();
          const ds = internalSchema.dataSource;
          if (ds?.saveSuccessMessage) {
            notify.success(ds.saveSuccessMessage);
          }
          if (ds?.onSaveSuccess === 'toastAndRefetch') {
            refreshTableFn?.();
          }
          onRowSaved?.(mergedRow, rowKey, 'update');
        } catch (error: any) {
          const fieldErrors = mapSaveActionErrors(error, [rowKey]);
          if (fieldErrors) {
            stateActions.setServerErrors(fieldErrors);
          } else {
            showApiErrorToast(error);
          }
        } finally {
          setInternalSaveLoading(false);
          dismissSaveConfirm();
        }
      } else if (internalSchema.dataSource?.saveEndpoint) {
        const ds = internalSchema.dataSource;
        const endpoint = ds.saveEndpoint!;
        const payload = applyPayloadMapper([mergedRow], ds.savePayloadMapper);
        setInternalSaveLoading(true);
        try {
          const result = await executeSaveAction(
            payload,
            endpoint,
            ds.saveMethod,
            ds.onSaveSuccess,
            ds.saveSuccessMessage
          );
          const saveOk = result?.ok === true;
          if (saveOk) {
            cleanupEditing();
            stateActions.clearServerErrors();
            onRowSaved?.(mergedRow, rowKey, 'update');
          } else {
            const fieldErrors: Array<{ sNo: string | null; field: string; message: string }> =
              result?.errors ?? [];
            const hasInlineErrors = fieldErrors.some((e) => e.field);
            if (hasInlineErrors) {
              const errorMap = new Map<string, Record<string, string>>();
              for (const e of fieldErrors) {
                if (!e.field) continue;
                const targetKey =
                  e.sNo != null && e.sNo.includes('.')
                    ? `${rowKey}.${e.sNo.split('.')[1]}`
                    : rowKey;
                const existing = errorMap.get(targetKey) ?? {};
                existing[e.field] = e.message;
                errorMap.set(targetKey, existing);
              }
              stateActions.setServerErrors(errorMap);
            }
          }
        } finally {
          setInternalSaveLoading(false);
          dismissSaveConfirm();
        }
      } else {
        dismissSaveConfirm();
        cleanupEditing();
      }
    },
    [
      tableState.editingRows,
      stateActions,
      onSave,
      displayData,
      resolveRowKey,
      internalSchema.dataSource,
      internalSchema.features.rows.subRows?.dataField,
      tableState.data,
      executeSaveAction,
      refreshTableFn,
      confirmSave,
      dismissSaveConfirm,
      getSubRowKeysForParent,
      applyRowValidation,
      applyComputeRules,
      computeSourceFields,
      onRowSaved,
    ]
  );

  const handleRowCancel = useCallback(
    (rowKey: string) => {
      const subRowKeys = getSubRowKeysForParent(rowKey);
      if (subRowKeys.length > 0) {
        stateActions.cancelEditingBatch([rowKey, ...subRowKeys]);
      } else {
        stateActions.cancelEditing(rowKey);
      }
    },
    [stateActions, getSubRowKeysForParent]
  );

  const handleBulkSave = useCallback(async () => {
    const liveState = getTableState?.() ?? tableState;
    const editingRows = liveState.editingRows;
    const editErrors = liveState.editErrors;
    const subField = internalSchema.features.rows.subRows?.dataField;
    const parentKeys = Array.from(editingRows.keys()).filter((k) => !k.includes('.'));
    const dirtyParentKeys = parentKeys.filter((rowKey) => {
      const edits = editingRows.get(rowKey);
      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === rowKey;
      }) as Record<string, unknown> | undefined;

      return rowHasSemanticDeltas(original ?? {}, edits);
    });

    const nonDirtyRowsWithErrors = Array.from(editErrors.entries()).filter(
      ([key, errs]) =>
        !key.includes('.') && !dirtyParentKeys.includes(key) && Object.keys(errs).length > 0
    );

    if (nonDirtyRowsWithErrors.length > 0) {
      return;
    }

    if (dirtyParentKeys.length === 0) {
      return;
    }

    const mergedRows: Record<string, unknown>[] = dirtyParentKeys.map((rowKey) => {
      const parentEdits = editingRows.get(rowKey) ?? {};
      let parentSNo = -1;
      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        if (resolveRowKey(rec, idx) === rowKey) { parentSNo = idx; return true; }
        return false;
      }) as Record<string, unknown> | undefined;

      let mergedRow: Record<string, unknown> = mergeRowForValidation(
        original,
        parentEdits,
        applyComputeRules,
        computeSourceFields,
        internalSchema.computeRules
      );

      if (subField) {
        const subRowKeys = getSubRowKeysForParent(rowKey);
        if (subRowKeys.length > 0) {
          const originalSubRows = (mergedRow[subField] as unknown[] | undefined) ?? [];
          const updatedSubRows = originalSubRows.map((sr, idx) => {
            const subKey = `${rowKey}.${idx}`;
            const subEdits = editingRows.get(subKey);
            const merged = subEdits ? { ...(sr as Record<string, unknown>), ...subEdits } : (sr as Record<string, unknown>);
            return parentSNo !== -1 ? { ...merged, sNo: `${parentSNo}.${idx}` } : merged;
          });
          mergedRow = { ...mergedRow, [subField]: updatedSubRows };
        }
      }

      if (parentSNo !== -1) {
        mergedRow = { ...mergedRow, sNo: String(parentSNo) };
      }

      return mergedRow;
    });

    const validationRows = dirtyParentKeys.map((rowKey, index) => ({
      rowKey,
      mergedRow: mergedRows[index],
    }));

    if (!applyRowValidation(validationRows)) return;

    const confirmed = await confirmSave();
    if (!confirmed) return;

    if (onSave) {
      setInternalSaveLoading(true);
      try {
        await onSave(mergedRows, liveState.data);
        stateActions.cancelAllEditing();
        stateActions.clearServerErrors();
        for (let i = 0; i < mergedRows.length; i++) {
          onRowSaved?.(mergedRows[i], dirtyParentKeys[i], 'update');
        }
      } catch (error) {
        showApiErrorToast(error);
      } finally {
        setInternalSaveLoading(false);
        dismissSaveConfirm();
      }
      return;
    } else if (
      internalSchema.dataSource?.saveActionId &&
      ActionRegistry.hasAction(internalSchema.dataSource.saveActionId)
    ) {
      const actionId = internalSchema.dataSource.saveActionId;
      setInternalSaveLoading(true);
      try {
        await ActionRegistry.executeActionRaw(actionId, {
          pk: '',
          row: {},
          data: { editedRows: mergedRows },
          refreshTable: refreshTableFn ?? (() => {}),
        });
        stateActions.cancelAllEditing();
        stateActions.clearServerErrors();
        const ds = internalSchema.dataSource;
        if (ds?.saveSuccessMessage) {
          notify.success(ds.saveSuccessMessage);
        }
        if (ds?.onSaveSuccess === 'toastAndRefetch') {
          refreshTableFn?.();
        }
        for (let i = 0; i < mergedRows.length; i++) {
          onRowSaved?.(mergedRows[i], dirtyParentKeys[i], 'update');
        }
      } catch (error: any) {
        const fieldErrors = mapSaveActionErrors(error, dirtyParentKeys);
        if (fieldErrors) {
          stateActions.setServerErrors(fieldErrors);
        } else {
          showApiErrorToast(error);
        }
      } finally {
        setInternalSaveLoading(false);
        dismissSaveConfirm();
      }
      return;
    } else if (internalSchema.dataSource?.saveEndpoint) {
      const ds = internalSchema.dataSource;
      const endpoint = ds.saveEndpoint!;
      const payload = applyPayloadMapper(mergedRows, ds.savePayloadMapper);
      setInternalSaveLoading(true);
      try {
        const result = await executeSaveAction(
          payload,
          endpoint,
          ds.saveMethod,
          ds.onSaveSuccess,
          ds.saveSuccessMessage
        );
        const saveOk = result?.ok === true;
        if (saveOk) {
          stateActions.cancelAllEditing();
          stateActions.clearServerErrors();
        } else {
          const fieldErrors: Array<{ sNo: string | null; field: string; message: string }> =
            result?.errors ?? [];
          const hasInlineErrors = fieldErrors.some((e) => e.field && e.sNo != null);
          if (hasInlineErrors) {
            const editingKeys = dirtyParentKeys;
            const errorMap = new Map<string, Record<string, string>>();
            for (const err of fieldErrors) {
              if (!err.field) continue;
              let targetKey: string | undefined;
              if (err.sNo != null && err.sNo.includes('.')) {
                const [parentIdx, subIdx] = err.sNo.split('.');
                const parentKey = editingKeys[Number(parentIdx)];
                targetKey = parentKey ? `${parentKey}.${subIdx}` : editingKeys[0];
              } else {
                targetKey =
                  err.sNo != null && editingKeys[Number(err.sNo)]
                    ? editingKeys[Number(err.sNo)]
                    : editingKeys[0];
              }
              if (!targetKey) continue;
              const existing = errorMap.get(targetKey) ?? {};
              existing[err.field] = err.message;
              errorMap.set(targetKey, existing);
            }
            stateActions.setServerErrors(errorMap);
          }
        }
      } finally {
        setInternalSaveLoading(false);
        dismissSaveConfirm();
      }
      return;
    }
    dismissSaveConfirm();
    stateActions.cancelAllEditing();
  }, [
    onSave,
    stateActions,
    internalSchema.dataSource,
    internalSchema.features.rows.subRows?.dataField,
    getTableState,
    tableState,
    displayData,
    resolveRowKey,
    getSubRowKeysForParent,
    executeSaveAction,
    refreshTableFn,
    confirmSave,
    dismissSaveConfirm,
    applyRowValidation,
    applyComputeRules,
    computeSourceFields,
    onRowSaved,
  ]);

  return {
    internalSaveLoading,
    effectiveSaveLoading,
    pendingSaveConfirm,
    handleRowSave,
    handleRowCancel,
    handleBulkSave,
  };
}
